import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { EventItem, AnnouncementItem } from "@/components/board/BoardItems";
import { EventForm } from "@/components/board/EventForm";
import { SlotList } from "@/components/board/EventExtras";
import { StatusDialog } from "@/components/board/EventStatus";
import { MakeupPoll } from "@/components/board/MakeupPoll";
import { VolunteerSection } from "@/components/board/VolunteerRoles";
import { copyJoin } from "@/components/coach/TeamCard";

const RsvpList = ({ rsvps }) => (
  <details className="text-sm" data-testid="event-rsvp-list">
    <summary className="font-bold cursor-pointer">Who's coming ({rsvps.length} replies)</summary>
    <ul className="mt-1 space-y-0.5">
      {rsvps.map((r) => <li key={r.child_name} className="font-semibold">{r.child_name}: {r.status === "yes" ? "Going" : r.status === "no" ? "Can't" : "Maybe"}</li>)}
      {!rsvps.length && <li className="text-[var(--muted)]">No replies yet.</li>}
    </ul>
  </details>
);

export default function TeamBoardPage() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [events, setEvents] = useState([]);
  const [anns, setAnns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [alerting, setAlerting] = useState(null);
  const [roles, setRoles] = useState([]);
  const [ann, setAnn] = useState({ title: "", body: "", pinned: false });
  const canEdit = user.access.has_access;

  const load = () => { api.events(teamId).then(setEvents); api.announcements(teamId).then(setAnns); api.volunteers(teamId).then(setRoles); };
  useEffect(() => { api.team(teamId).then(setTeam); load(); }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const guard = (p, msg) => p.then(() => { toast.success(msg); load(); }, (e) => { toast.error(formatApiError(e)); throw e; });
  const saveEvent = (body) => guard(editing.id ? api.updateEvent(teamId, editing.id, body) : api.createEvent(teamId, body), "Event saved").then(() => setEditing(null));
  const saveStatus = (body) => guard(api.setEventStatus(teamId, alerting.id, body), body.status === "scheduled" ? "Alert cleared" : "Parents alerted").then(() => setAlerting(null), () => {});
  const roleAction = (p, msg) => p.then((v) => { setRoles(v); toast.success(msg); }, (e) => { toast.error(formatApiError(e)); throw e; });
  const postAnn = (e) => {
    e.preventDefault();
    guard(api.createAnnouncement(teamId, ann), "Announcement posted").then(() => setAnn({ title: "", body: "", pinned: false }), () => {});
  };

  if (!team) return <div className="min-h-screen grid place-items-center font-heading text-2xl">Loading...</div>;
  return (
    <PageShell>
      <AppHeader><Link to={`/coach/teams/${teamId}`} className="nav-btn" data-testid="board-back-link"><ArrowLeft size={18} /> Playbook</Link></AppHeader>
      <div className="team-banner">
        <div className="min-w-0">
          <p className="ctl-label">Team board · parents see this on the team link</p>
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold uppercase tracking-tight truncate">{team.name}</h1>
        </div>
        <button className="btn-ghost" data-testid="board-copy-join-button" onClick={() => copyJoin(team.join_code)}>Copy parent link</button>
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr] items-start">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-heading text-3xl font-extrabold uppercase">Schedule</h2>
            {canEdit && <button className="btn-primary" data-testid="new-event-button" onClick={() => setEditing({})}><Plus size={18} /> New event</button>}
          </div>
          {events.length === 0 && <div className="card font-semibold" data-testid="coach-events-empty">No events yet. Add practices and games so parents know when and where.</div>}
          {events.map((e) => (
            <EventItem key={e.id} event={e}>
              <MakeupPoll event={e} onPick={canEdit ? (o) => guard(api.pickMakeup(teamId, e.id, o.id), "Makeup event created").catch(() => {}) : null} />
              <RsvpList rsvps={e.rsvps} />
              {e.slots.length > 0 && <SlotList slots={e.slots} />}
              {canEdit && (
                <div className="flex flex-wrap gap-2">
                  <button className="btn-ghost !min-h-[40px] !text-sm" data-testid="event-edit-button" onClick={() => setEditing(e)}><Pencil size={16} /> Edit</button>
                  <button className="btn-ghost !min-h-[40px] !text-sm" data-testid="event-alert-button" onClick={() => setAlerting(e)}><AlertTriangle size={16} /> {e.status === "scheduled" ? "Cancel / move" : "Update alert"}</button>
                  <button className="btn-ghost !min-h-[40px] !text-sm" data-testid="event-delete-button" onClick={() => guard(api.deleteEvent(teamId, e.id), "Event deleted").catch(() => {})}><Trash2 size={16} /> Delete</button>
                </div>
              )}
            </EventItem>
          ))}
        </section>
        <section className="space-y-3">
          <h2 className="font-heading text-3xl font-extrabold uppercase">Announcements</h2>
          {canEdit && (
            <form className="card space-y-2" onSubmit={postAnn} data-testid="announcement-form">
              <input data-testid="announcement-title-input" className="field-input w-full" required maxLength={100} placeholder="Title, e.g. Picture day Saturday" value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} />
              <textarea data-testid="announcement-body-input" className="field-input w-full min-h-[90px]" required maxLength={2000} placeholder="Message for parents" value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })} />
              <label className="flex items-center gap-2 font-semibold"><input type="checkbox" className="w-5 h-5" data-testid="announcement-pin-checkbox" checked={ann.pinned} onChange={(e) => setAnn({ ...ann, pinned: e.target.checked })} /> Pin to top</label>
              <button className="btn-primary w-full" data-testid="announcement-post-button">Post announcement</button>
            </form>
          )}
          {anns.map((a) => (
            <AnnouncementItem key={a.id} a={a}>
              <button className="icon-btn !min-h-[36px] !min-w-[36px]" data-testid="announcement-delete-button" aria-label="Delete announcement"
                onClick={() => guard(api.deleteAnnouncement(teamId, a.id), "Announcement deleted").catch(() => {})}><Trash2 size={16} /></button>
            </AnnouncementItem>
          ))}
        </section>
      </div>
      <div className="mt-6">
        <VolunteerSection roles={roles}
          onToggle={canEdit ? (r, applies) => roleAction(api.updateRole(teamId, r.id, applies), "Role updated").catch(() => {}) : null}
          onAdd={canEdit ? (body) => roleAction(api.createRole(teamId, body), "Role added") : null}
          onDelete={(r) => window.confirm(`Delete ${r.label} and its signups?`) && roleAction(api.deleteRole(teamId, r.id), "Role deleted").catch(() => {})}
          onRemove={(r, name) => roleAction(api.removeVolunteer(teamId, r.id, name), `Removed ${name}`).catch(() => {})} />
      </div>
      {alerting && <StatusDialog event={alerting} onClose={() => setAlerting(null)} onSave={saveStatus} />}
      {editing && <EventForm initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSave={saveEvent} />}
    </PageShell>
  );
}
